<template>
  <div class="personnel-view">
    <!-- Header -->
    <Header title="Gestion du Personnel" />
    
    <div class="personnel-content">
      <h4>📋 Effectifs et statistiques</h4>

    <!-- Stats Cards & Charts -->
    <div class="dashboard-section">
      <div class="stats-cards">
        <div class="stat-card total-card">
          <div class="stat-icon">
            <span class="material-icons">people</span>
          </div>
          <div class="stat-content">
            <h3>{{ personnelStore.personnel.total || 0 }}</h3>
            <p>Total Personnel</p>
          </div>
        </div>
        
        <div class="stat-card male-card">
          <div class="stat-icon">
            <span class="material-icons">man</span>
          </div>
          <div class="stat-content">
            <h3>{{ personnelStore.personnel.countByGender?.M || 0 }}</h3>
            <p>Hommes</p>
            <span class="stat-percentage" v-if="personnelStore.personnel.total > 0">
              {{ Math.round((personnelStore.personnel.countByGender?.M || 0) / personnelStore.personnel.total * 100) }}%
            </span>
          </div>
          <div class="stat-progress" v-if="personnelStore.personnel.total > 0">
            <div class="stat-bar" :style="{ width: `${Math.round((personnelStore.personnel.countByGender?.M || 0) / personnelStore.personnel.total * 100)}%` }"></div>
          </div>
        </div>
        
        <div class="stat-card female-card">
          <div class="stat-icon">
            <span class="material-icons">woman</span>
          </div>
          <div class="stat-content">
            <h3>{{ personnelStore.personnel.countByGender?.F || 0 }}</h3>
            <p>Femmes</p>
            <span class="stat-percentage" v-if="personnelStore.personnel.total > 0">
              {{ Math.round((personnelStore.personnel.countByGender?.F || 0) / personnelStore.personnel.total * 100) }}%
            </span>
          </div>
          <div class="stat-progress" v-if="personnelStore.personnel.total > 0">
            <div class="stat-bar" :style="{ width: `${Math.round((personnelStore.personnel.countByGender?.F || 0) / personnelStore.personnel.total * 100)}%` }"></div>
          </div>
        </div>
        
        <div class="stat-card direction-card">
          <div class="stat-icon">
            <span class="material-icons">business</span>
          </div>
          <div class="stat-content">
            <h3>{{ directionsCount }}</h3>
            <p>Directions</p>
          </div>
          <div class="stat-action">
            <button @click="refreshDirectionChart" class="refresh-btn" title="Rafraîchir les données">
              <span class="material-icons">refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div class="charts-container">
        <div class="chart-card" id="direction-chart-container">
          <div class="chart-header">
            <h3>Personnel par direction</h3>
          </div>
          <div class="chart-content">
            <canvas id="direction-chart"></canvas>
          </div>
        </div>
        
        <div class="chart-card" id="grade-chart-container">
          <div class="chart-header">
            <h3>Personnel par grade</h3>
          </div>
          <div class="chart-content">
            <canvas id="grade-chart"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Control Panel -->
    <div class="control-panel">
      <div class="panel-header">
        <h3>Liste du personnel</h3>
        <span class="result-count" v-if="personnelStore.filteredPersonnel.length">
          {{ personnelStore.filteredPersonnel.length }} agent(s)
        </span>
      </div>
      
      <div class="search-filters">
        <div class="search-box">
          <span class="material-icons">search</span>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Rechercher un agent..." 
            @input="handleSearch"
          >
        </div>
        <div class="filter-dropdown">
          <label>Direction:</label>
          <div class="select-wrapper">
            <select v-model="selectedDirection" @change="handleDirectionChange">
              <option value="Toutes">Toutes les directions</option>
              <option v-for="direction in directionsList" :key="direction.id" :value="direction.id">
                {{ direction.nom }}
              </option>
            </select>
            <span class="material-icons select-icon">expand_more</span>
          </div>
        </div>
      </div>
      
      <div class="action-buttons">
        <button @click="createNewAgent" class="action-btn btn-primary">
          <span class="material-icons">person_add</span>
          <span class="btn-text">Nouvel agent</span>
        </button>
        <button class="action-btn btn-secondary" @click="exportExcel" :disabled="personnelStore.filteredPersonnel.length === 0">
          <span class="material-icons">file_download</span>
          <span class="btn-text">Exporter en Excel</span>
        </button>
      </div>
    </div>

    <!-- Personnel Table -->
    <div class="table-container">
      <div class="table-responsive">
        <table class="personnel-table">
          <thead>
            <tr>
              <th>
                <div class="th-content">
                  <span class="material-icons">format_list_numbered</span>
                  N°
                </div>
              </th>
              <th>
                <div class="th-content">
                  <span class="material-icons">badge</span>
                  Matricule
                </div>
              </th>
              <th>
                <div class="th-content">
                  <span class="material-icons">person</span>
                  Nom
                </div>
              </th>
              <th>
                <div class="th-content">
                  <span class="material-icons">wc</span>
                  Sexe
                </div>
              </th>
              <th>
                <div class="th-content">
                  <span class="material-icons">military_tech</span>
                  Grade
                </div>
              </th>

              <th>
                <div class="th-content">
                  <span class="material-icons">settings</span>
                  Actions
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- Début du tableau personnel -->
            <tr v-if="personnelStore.loading">
              <td colspan="7" style="text-align: center; padding: 30px;">
                <div style="display: inline-block; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 30px; height: 30px; animation: spin 2s linear infinite;"></div>
                <p style="margin-top: 10px;">Chargement en cours...</p>
              </td>
            </tr>
            <tr v-else-if="personnelStore.paginatedPersonnel.length === 0">
              <td colspan="7" class="no-data">
                <div class="empty-state">
                  <span class="material-icons">person_off</span>
                  <p>Aucun agent trouvé</p>
                  <small v-if="searchQuery || selectedDirection !== 'Toutes'">Essayez de modifier vos filtres de recherche</small>
                </div>
              </td>
            </tr>
            <tr v-else v-for="(agent, index) in personnelStore.paginatedPersonnel" :key="agent.num_personnel || agent.id">
              <td class="text-center">
                <div class="order-number">
                  {{ (personnelStore.pagination.page - 1) * personnelStore.pagination.limit + index + 1 }}
                </div>
              </td>
              <td>
                <div class="user-badge">
                  <span class="matricule">{{ agent.matricule }}</span>
                </div>
              </td>
              <td>
                <div class="agent-name-container">
                  <div class="agent-photo">
                    <img 
                      :src="getPhotoUrl(agent.photo)" 
                      alt="Photo de l'agent"
                      @error="handleImageError"
                    />
                  </div>
                  <span class="capitalize">{{ agent.nom }}</span>
                </div>
              </td>
              <td class="text-center">
                <div class="badge-cell">
                  <span :class="`gender-badge ${agent.sexe === 'M' ? 'male' : 'female'}`">
                    {{ agent.sexe === 'M' ? 'H' : 'F' }}
                  </span>
                </div>
              </td>
              <td>
                <div class="badge-cell">
                  <span class="grade-badge">{{ gradeTable[agent.grade_id] || '-' }}</span>
                </div>
              </td>

              <td>
                <div class="action-buttons">
                  <router-link :to="`/agent/${agent.num_personnel}`" class="view-btn" title="Voir le profil">
                    <span class="material-icons">visibility</span>
                  </router-link>
                  <button class="edit-btn" @click="editAgent(agent)" title="Modifier">
                    <span class="material-icons">edit</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination -->
      <div class="pagination-container" v-if="personnelStore.filteredPersonnel.length > 0">
        <div class="pagination-info">
          Affichage de {{ paginationInfo.start }} à {{ paginationInfo.end }} sur {{ personnelStore.filteredPersonnel.length }} agents
        </div>
        <div class="pagination-controls">
          <button 
            class="page-btn prev" 
            :disabled="personnelStore.pagination.page === 1"
            @click="goToPage(personnelStore.pagination.page - 1)"
          >
            <span class="material-icons">chevron_left</span>
          </button>
          <button 
            v-for="page in paginationPages" 
            :key="page"
            class="page-btn" 
            :class="{ active: personnelStore.pagination.page === page }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
          <button 
            class="page-btn next"
            :disabled="personnelStore.pagination.page === personnelStore.pagination.totalPages"
            @click="goToPage(personnelStore.pagination.page + 1)"
          >
            <span class="material-icons">chevron_right</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Modal de capture de photo -->
    <photo-capture-modal 
      :show="isPhotoModalVisible" 
      :title="`Photo de l'agent: ${selectedAgent?.nom || ''}`"
      :agent-id="selectedAgent?.id"
      @close="isPhotoModalVisible = false"
      @photo-updated="handlePhotoUpdate"
    />
  </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { usePersonnelStore } from '@/store/personnel'
import { useAuthStore } from '@/store/auth'
import { useToast } from '@/composables/useToast'
import api from '@/services/api'
//import { logoBase64 } from '@/assets/logo-base64'
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import PhotoCaptureModal from '@/components/PhotoCaptureModal.vue'
import Header from '@/components/Header.vue'

// Enregistrement du plugin datalabels pour l'affichage des pourcentages
Chart.register(ChartDataLabels)

// Stores & Router
const router = useRouter()
const authStore = useAuthStore()
const personnelStore = usePersonnelStore()
const toast = useToast()

// Reactive state
const searchQuery = ref('')
const selectedDirection = ref('Toutes')
const showAddAgentModal = ref(false)
const directionChart = ref(null)
const gradeChart = ref(null)
const directionsCount = ref(0) // Variable réactive pour stocker le nombre de directions
const apiDirectionsList = ref([]) // Variable réactive pour stocker les directions depuis l'API

const gradeTable = {}
// Fonction pour rafraîchir le graphique des directions
const refreshDirectionChart = async () => { 
  
  // Forcer un rechargement complet des données depuis l'API
  try {
    // Réinitialiser le graphique existant
    if (directionChart.value) {
      directionChart.value.destroy()
      directionChart.value = null
    }
    
    // Récupérer les données fraîches de l'API
    const freshData = await api.getPersonnelByDirection() 
    
    if (freshData && freshData.data && Array.isArray(freshData.data.data)) {
      // Mettre à jour le nombre de directions
      directionsCount.value = freshData.data.data.length
      
      // Initialiser le graphique avec les nouvelles données
      await initDirectionChart()
      
      // Afficher un toast de confirmation
      toast.showSuccess('Graphique des directions mis à jour avec succès')
    } else {
      toast.showError('Erreur lors du chargement des données des directions')
    }
  } catch (error) {
    console.error('Error refreshing direction chart:', error)
    toast.showError('Erreur lors de la mise à jour du graphique des directions')
  }
}

// Computed properties
const personnelData = computed(() => {
  if (!personnelStore.personnel || !personnelStore.personnel.data) {
    return []
  }
  return personnelStore.personnel.data.slice(0, 5)
})

const directionsList = computed(() => {
  // Si nous avons des directions depuis l'API, les utiliser en priorité
  if (apiDirectionsList.value && apiDirectionsList.value.length > 0) {
    return [...new Set(apiDirectionsList.value)].sort()
  }   
    return [] 

})

const paginationInfo = computed(() => {
  const page = personnelStore.pagination.page
  const limit = personnelStore.pagination.limit
  const total = personnelStore.filteredPersonnel.length
  
  const start = (page - 1) * limit + 1
  const end = Math.min(start + limit - 1, total)
  
  return { start, end }
})

const paginationPages = computed(() => {
  const current = personnelStore.pagination.page
  const total = personnelStore.pagination.totalPages
  
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  
  if (current <= 3) {
    return [1, 2, 3, 4, 5]
  }
  
  if (current >= total - 2) {
    return [total - 4, total - 3, total - 2, total - 1, total]
  }
  
  return [current - 2, current - 1, current, current + 1, current + 2]
})

// Methods
// Récupère les données par grade depuis l'API
const fetchGradeData = async () => {
  try {
    // Utilisation de l'endpoint dédié pour les données par grade
    const response = await api.getPersonnelByGrade() 
    
    if (response && response.data && Array.isArray(response.data.data)) {
      // Conversion du format API en format adapté pour le graphique
      const gradeData = {}      
      response.data.data.forEach(grade => {
        if (grade.libelle && grade.nombre_agents !== undefined) {
          gradeData[grade.libelle] = grade.nombre_agents
          gradeTable[grade.id] = grade.libelle
        }
      })
      
      console.log('Grade data from API:', gradeData)
      return gradeData
    }
    
    // Fallback: calculer à partir des données des agents si l'API échoue
    const personnelData = personnelStore.personnel.data || []
    
    if (!personnelData.length) {
      console.error('No personnel data available for grade chart')
      return null
    }
    
    const gradeData = {}
    personnelData.forEach(agent => {
      const grade = agent.grade || 'Non défini'
      gradeData[grade] = (gradeData[grade] || 0) + 1
    })
    
    return gradeData
  } catch (error) {
    console.error('Error preparing grade data:', error)
    return null
  }
}

// Initialisation du graphique par grade
const initGradeChart = async () => {
  //console.log('Initializing grade chart')
  
  // S'assurer que le canvas existe
  const canvas = document.getElementById('grade-chart')
  if (!canvas) {
    console.error('Grade chart canvas not found')
    return
  }
  
  // Détruire l'ancien graphique s'il existe
  if (gradeChart.value) {
    gradeChart.value.destroy()
    gradeChart.value = null
  }
  
  // Obtenir les données pour le graphique
  const gradeData = await fetchGradeData()
  
  if (!gradeData || Object.keys(gradeData).length === 0) {
    //  console.error('No grade data available')
    
    // Afficher un message d'erreur dans le canvas
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.font = '14px Arial'
    ctx.fillStyle = '#f44336'
    ctx.textAlign = 'center'
    ctx.fillText('Aucune donnée disponible sur les grades', canvas.width / 2, canvas.height / 2)
    return
  }
  
  const ctx = canvas.getContext('2d')
  
  // Préparer les données pour le graphique
  const labels = Object.keys(gradeData)
  const data = labels.map(key => gradeData[key])
  
  // Aucun code supplémentaire nécessaire ici pour les grades
  
  // Palette de couleurs élégantes et sophistiquées
  const elegantGradeColors = [
    '#8266d4', // Violet royal
    '#4faeba', // Turquoise
    '#e07a5f', // Terracotta
    '#3d405b', // Bleu nuit
    '#81b29a', // Vert sauge
    '#f2cc8f', // Doré
    '#cd5c5c', // Rouge indien
    '#7ba3cc', // Bleu ciel
    '#ca9272', // Camel
    '#896bb3', // Lavande
    '#5e8c61', // Vert forêt
    '#eaac8b', // Pêche
  ];
  
  // Garantir suffisamment de couleurs en répétant la palette si nécessaire
  const colors = labels.map((_, i) => elegantGradeColors[i % elegantGradeColors.length]);
  
  // Créer le graphique avec une animation et un design moderne
  gradeChart.value = new Chart(ctx, {
    type: 'pie', // Utiliser un graphique en camembert pour les grades
    data: {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: colors,
        borderColor: '#ffffff',
        borderWidth: 3,
        hoverOffset: 15,
        borderRadius: 4
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        animateRotate: true,
        animateScale: true,
        duration: 1500,
        easing: 'easeOutQuart'
      },
      elements: {
        arc: {
          borderWidth: 2
        }
      },
      plugins: {
        legend: {
          position: 'right',
          title: {
            display: true,
            text: 'Grades',
            font: {
              family: "'Roboto', 'Helvetica', 'Arial', sans-serif",
              size: 14,
              weight: 'bold'
            },
            padding: 10
          },
          labels: {
            boxWidth: 15,
            padding: 15,
            usePointStyle: true,
            pointStyle: 'circle',
            font: {
              family: "'Roboto', 'Helvetica', 'Arial', sans-serif",
              size: 12
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: {
            family: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            size: 13
          },
          bodyFont: {
            family: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            size: 12
          },
          padding: 12,
          cornerRadius: 6,
          callbacks: {
            label: function(context) {
              const value = context.raw
              const total = data.reduce((a, b) => a + b, 0)
              const percentage = ((value / total) * 100).toFixed(1)
              return `${context.label}: ${value} agents (${percentage}%)`
            }
          }
        },
        datalabels: {
          display: function(context) {
            // Vérifier que les données sont valides
            if (!context || !context.dataset || !context.dataset.data || context.dataset.data.length === 0) {
              return false;
            }
            // S'assurer que le total n'est pas zéro pour éviter les divisions par zéro
            const total = context.dataset.data.reduce((a, b) => a + (b || 0), 0);
            if (total === 0) return false;
            // Afficher les pourcentages seulement pour les segments plus grands que 3%
            return (context.dataset.data[context.dataIndex] || 0) / total > 0.03;
          },
          formatter: function(value, context) {
            if (!context || !context.dataset || !context.dataset.data || context.dataset.data.length === 0) {
              return '';
            }
            const total = context.dataset.data.reduce((a, b) => a + (b || 0), 0);
            if (total === 0) return '0%';
            const percentage = (((value || 0) / total) * 100).toFixed(1);
            return percentage + '%';
          },
          color: '#fff',
          font: {
            weight: 'bold',
            size: 11,
            family: "'Roboto', 'Helvetica', 'Arial', sans-serif"
          },
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          borderRadius: 4,
          padding: { top: 2, bottom: 2, left: 4, right: 4 },
          textStrokeColor: 'black',
          textStrokeWidth: 0.5,
          align: 'center',
          anchor: 'center'
        }
      }
    }
  })
}

const fetchPersonnel = async () => {
  await personnelStore.fetchPersonnel()
  await initGradeChart() // Initialiser le graphique par grade
}

const forceRefresh = async () => {
  //console.log('Force refreshing personnel data...')
  await fetchPersonnel()
}

const handleSearch = () => {
  personnelStore.setSearchQuery(searchQuery.value)
  personnelStore.setPage(1) // Reset to first page on search
}

const handleDirectionChange = () => {
  console.log('Direction sélectionnée:', selectedDirection.value);
  
  // Transmettre la direction sélectionnée au store
  personnelStore.setSelectedDirection(selectedDirection.value === 'Toutes' ? 'Toutes' : selectedDirection.value)
  
  // Réinitialiser la pagination à la première page
  personnelStore.setPage(1)
}

const goToPage = (page) => {
  personnelStore.setPage(page)
}

const createNewAgent = () => {
  // Rediriger vers la page de création d'un nouvel agent
  router.push('/agent/new')
}

const editAgent = (agent) => {
  // Rediriger vers la page d'édition d'agent
  router.push(`/agent/edit/${agent.num_personnel}`)
}

// Fonction pour gérer les erreurs de chargement d'image
const handleImageError = (event) => {
  event.target.src = require('@/assets/images/user-default.png');
}

// Fonction pour construire l'URL de la photo
const getPhotoUrl = (photoPath) => {
  const api_url = api.getApiUrl();
  
  if (!photoPath) {
    return require('@/assets/images/user-default.png');
  }
  
  // Si c'est une chaîne base64 (nouvelle photo sélectionnée ou capturée)
  if (typeof photoPath === 'string' && photoPath.startsWith('data:image')) {
    return photoPath;
  }
  
  // Si c'est un objet File (nouvelle photo sélectionnée)
  if (photoPath instanceof File) {
    return URL.createObjectURL(photoPath);
  }
  
  // Si c'est déjà une URL complète (http ou https)
  if (typeof photoPath === 'string' && photoPath.startsWith('http')) {
    return photoPath;
  }
  
  // Si c'est juste un nom de fichier, construire l'URL complète
  if (typeof photoPath === 'string' && !photoPath.startsWith('/')) {
    return `${api_url}${photoPath}`;
  }
  
  // Si c'est un chemin relatif commençant par /, ajouter le préfixe de l'API
  if (typeof photoPath === 'string') {
    return `${api_url}${photoPath}`;
  }
  
  // Cas par défaut
  return require('@/assets/images/user-default.png');
}

const exportExcel = async () => {
  try {
    // Importer les bibliothèques nécessaires de manière dynamique
    const ExcelJS = (await import('exceljs')).default
    const FileSaver = (await import('file-saver'))

    // Créer un nouveau classeur Excel
    const workbook = new ExcelJS.Workbook()
    
    // Métadonnées du document
    workbook.creator = 'OpSolver'
    workbook.lastModifiedBy = 'OpSolver Frontend'
    workbook.created = new Date()
    workbook.modified = new Date()
    
    // Récupérer les données du personnel
    const personnel = personnelStore.filteredPersonnel || []
    
    // Si aucune donnée n'est disponible
    if (!personnel.length) {
      alert('Aucune donnée à exporter')
      return
    }

    // Récupérer la liste des directions
    const directions = [...new Set(personnel.map(agent => agent.direction || 'Non défini'))].sort()
    
    // Date actuelle pour le nom du fichier
    const now = new Date()
    const dateStr = `${now.getDate().toString().padStart(2, '0')}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}`
    
    // Créer une feuille principale avec le résumé
    const summarySheet = workbook.addWorksheet('Résumé')
    
    // En-tête du document avec logo et titre
    summarySheet.getColumn('A').width = 8  // Colonne N°
    summarySheet.getColumn('B').width = 30 // Direction
    summarySheet.getColumn('C').width = 20 // Nombre d'agents
    summarySheet.getColumn('D').width = 15 // Pourcentage
    summarySheet.getColumn('E').width = 15 // Extra
    summarySheet.getRow(1).height = 80
    
  
    
    // Titre du document
    summarySheet.mergeCells('A1:D1')
    const titleCell = summarySheet.getCell('A1')
    titleCell.value = 'LISTE DU PERSONNEL'
    titleCell.font = {
      name: 'Arial',
      size: 18,
      bold: true,
      color: { argb: '2F3E5D' }
    }
    titleCell.alignment = {
      vertical: 'middle',
      horizontal: 'center'
    }
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'EEEEEE' }
    }
    
    // Informations générales
    summarySheet.addRow([])
    summarySheet.addRow(['Date d\'export', dateStr])
    summarySheet.addRow(['Nombre total d\'agents', personnel.length.toString()])
    summarySheet.addRow(['Nombre de directions', directions.length.toString()])
    
    // Appliquer style aux cellules d'information
    for (let i = 3; i <= 5; i++) {
      const row = summarySheet.getRow(i)
      row.getCell(1).font = { bold: true }
      row.height = 25
      row.alignment = { vertical: 'middle' }
    }
    
    // Ajouter un tableau récapitulatif des effectifs par direction
    summarySheet.addRow([])
    summarySheet.addRow(['EFFECTIFS PAR DIRECTION'])
    summarySheet.getRow(7).font = { bold: true, size: 14 }
    summarySheet.addRow(['N°', 'Direction', 'Nombre d\'agents', 'Pourcentage'])
    
    // Styles pour l'en-tête du tableau récapitulatif
    const headerRow = summarySheet.getRow(8)
    headerRow.height = 30
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' }
    headerRow.font = { bold: true }
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '4F81BD' }
      }
      cell.font = {
        bold: true,
        color: { argb: 'FFFFFF' }
      }
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }
    })
    
    // Ajouter les données par direction
    const directionCounts = {}
    personnel.forEach(agent => {
      const direction = agent.direction || 'Non défini'
      directionCounts[direction] = (directionCounts[direction] || 0) + 1
    })
    
    const rows = []
    directions.forEach(direction => {
      const count = directionCounts[direction] || 0
      const percentage = ((count / personnel.length) * 100).toFixed(1) + '%'
      rows.push([direction, count, percentage])
    })
    
    // Trier par nombre d'agents décroissant
    rows.sort((a, b) => b[1] - a[1])
    
    // Ajouter les lignes triées au tableau
    rows.forEach((rowData, index) => {
      // Ajouter le numéro d'ordre au début du tableau
      const rowWithNumber = [index + 1, ...rowData]
      const row = summarySheet.addRow(rowWithNumber)
      row.height = 25
      row.alignment = { vertical: 'middle' }
      row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' } // N°
      row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' }   // Direction
      row.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' } // Nombre d'agents
      row.getCell(4).alignment = { horizontal: 'center', vertical: 'middle' } // Pourcentage
      
      // Bordures pour toutes les cellules
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      })
    })
    
    // Ajouter le total
    const totalRow = summarySheet.addRow(['', 'Total', personnel.length, '100%'])
    totalRow.height = 25
    totalRow.font = { bold: true }
    totalRow.alignment = { vertical: 'middle' }
    totalRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' } // Cellule vide pour N°
    totalRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' }   // Total
    totalRow.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' } // Nombre d'agents
    totalRow.getCell(4).alignment = { horizontal: 'center', vertical: 'middle' } // Pourcentage
    totalRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'double' },
        right: { style: 'thin' }
      }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E9EDF5' }
      }
    })
    
    // Créer une feuille par direction avec les détails des agents
    directions.forEach(direction => {
      // Limiter le nom de la feuille à 31 caractères maximum (limite Excel)
      let sheetName = direction
      if (sheetName.length > 30) {
        sheetName = sheetName.substring(0, 30)
      }
      const dirSheet = workbook.addWorksheet(sheetName)
      
      // Configurer les colonnes
      dirSheet.columns = [
        { header: 'N°', key: 'number', width: 7 },
        { header: 'Matricule', key: 'matricule', width: 15 },
        { header: 'Nom', key: 'nom', width: 45 },
        { header: 'Sexe', key: 'sexe', width: 10 },
        { header: 'Grade', key: 'grade', width: 20 },
        { header: 'Fonction', key: 'fonction', width: 25 },
        { header: 'Direction', key: 'direction', width: 45 },
        { header: 'Lieu de naissance', key: 'lieu_naissance', width: 30 },
        { header: 'Date de naissance', key: 'date_naissance', width: 20 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'Téléphone', key: 'telephone', width: 20 }
      ]
      
      // Style pour l'en-tête
      dirSheet.getRow(1).height = 30
      dirSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } }
      dirSheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' }
      dirSheet.getRow(1).eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '1D7A70' } // Turquoise très foncé #1D7A70
        }
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      })
      
      // Filtrer les agents par direction
      const dirAgents = personnel.filter(agent => (agent.direction || 'Non défini') === direction)
      
      // Ajouter les données des agents
      dirAgents.forEach((agent, index) => {
        const row = dirSheet.addRow({
          number: index + 1,
          matricule: agent.matricule || '',
          nom: agent.nom || '',
          sexe: agent.sexe || '',
          grade: agent.grade || '',
          fonction: agent.fonction || '',
          direction: agent.direction || 'Non défini',
          lieu_naissance: agent.lieu_naissance || '',
          date_naissance: agent.date_naissance || '',
          email: agent.email || '',
          telephone: agent.telephone || ''
        })
        
        row.height = 25
        row.alignment = { vertical: 'middle' }
        
        // Alterner les couleurs de ligne
        if (index % 2 !== 0) {
          row.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'F2F2F2' }
            }
          })
        }
        
        // Ajouter des bordures à toutes les cellules
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        })
      })
      
      // Ajouter des filtres de colonne
      dirSheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: 11 }
      }
    })
    
    // Générer le fichier Excel
    const buffer = await workbook.xlsx.writeBuffer()
    FileSaver.saveAs(new Blob([buffer]), `Personnel_OpSolver_${dateStr}.xlsx`)
    
    console.log('Export Excel terminé')
  } catch (error) {
    console.error('Erreur lors de l\'export Excel:', error)
    alert('Erreur lors de l\'export Excel')
  }
}

const fetchDirectionData = async () => {
  try {
    //console.log('Fetching direction data from API...')
    const response = await api.getPersonnelByDirection()
    //console.log('Direction data from API:', response.data)
    
    // Mettre à jour la variable réactive directionsCount avec le nombre de directions
    if (response && response.data && Array.isArray(response.data.data)) {
      directionsCount.value = response.data.data.length
       
      apiDirectionsList.value = response.data.data.map(item => item ) 
    }
    
    return response.data
  } catch (error) {
    console.error('Error fetching direction data:', error)
    return null
  }
}

// Chart initialization
const initDirectionChart = async () => {
  //console.log('Initializing direction chart')
  
  // S'assurer que le canvas existe
  const canvas = document.getElementById('direction-chart')
  if (!canvas) {
    console.error('Canvas element not found')
    return
  }
  
  // Détruire le graphique existant s'il existe
  if (directionChart.value) {
    directionChart.value.destroy()
    directionChart.value = null
  }
  
  console.log('Canvas found, initializing chart...')
  
  // Définir des données de test pour garantir l'affichage
  let staffByDirection = {
    'BD-NTIC': 2,
    'COORDINATION': 1,
    'DIRECTION D\'ADMINISTRATION': 1,
    'DIRECTION TEST': 0,
    'ETUDES ET PLANIFICATION': 0
  }
  
  try {
    // Essayer de récupérer les données depuis l'API
    console.log('Fetching direction data from API...')
    const response = await api.getPersonnelByDirection()
    console.log('API Response:', response)
    
    if (response && response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
      // Réinitialiser staffByDirection pour utiliser les données de l'API
      staffByDirection = {}
      
      // Traiter les données de l'API
      response.data.data.forEach(direction => {
        if (direction.nom) {
          // Convertir en nombre et utiliser 0 si non défini
          staffByDirection[direction.nom] = parseInt(direction.nombre_agents || '0') || 0
        }
      })
      
      // Mettre à jour le nombre de directions
      directionsCount.value = Object.keys(staffByDirection).length
      console.log('Using API data for chart:', staffByDirection)
    } else {
      console.warn('No valid data from API, using test data')
      // Garder les données de test définies plus haut
      directionsCount.value = Object.keys(staffByDirection).length
    }
  } catch (error) {
    console.error('Error fetching direction data:', error)
    // En cas d'erreur, garder les données de test
    directionsCount.value = Object.keys(staffByDirection).length
  }
  
  // Vérifier si nous avons des données à afficher
  if (Object.keys(staffByDirection).length === 0) {
    console.warn('No valid direction data available, using default test data')
    // Utiliser des données de test pour garantir l'affichage
    staffByDirection = {
      'BD-NTIC': 2,
      'COORDINATION': 1,
      'DIRECTION D\'ADMINISTRATION': 1,
      'DIRECTION TEST': 0,
      'ETUDES ET PLANIFICATION': 0
    }
    directionsCount.value = Object.keys(staffByDirection).length
  }
  
  console.log('Final direction data for chart:', staffByDirection)
  
  const ctx = canvas.getContext('2d')
  
  // Convertir les données en format approprié pour le graphique
  // Si staffByDirection est un tableau d'objets, le convertir en objet simple
  let chartData = {}
  
  if (Array.isArray(staffByDirection)) {
    //console.log('Converting array data to object format')
    staffByDirection.forEach(item => {
      if (item && item.direction && item.count !== undefined) {
        chartData[item.direction] = item.count
      }
    })
  } else {
    // Sinon, utiliser directement l'objet
    chartData = staffByDirection
  }
  
  //console.log('Final chart data structure:', chartData)
  
  // Si après conversion, nous n'avons toujours pas de données, essayons de récupérer au moins les noms des directions
  if (Object.keys(chartData).length === 0) {
    console.warn('No direction data after conversion, trying to get direction names for legends')
    // Récupérer à nouveau les données de l'API pour obtenir au moins les noms des directions
    try {
      const apiData = await api.getPersonnelByDirection()
      if (apiData && apiData.data && Array.isArray(apiData.data.data)) {
        apiData.data.data.forEach(direction => {
          if (direction.nom) {
            // Ajouter chaque direction avec une valeur de 0
            chartData[direction.nom] = 0
          }
        })
        console.log('Added direction names with zero values:', chartData)
      }
    } catch (error) {
      console.error('Error getting direction names:', error)
    }
    
    // Si toujours pas de données, afficher un message
    if (Object.keys(chartData).length === 0) {
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.font = '14px Arial'
      ctx.fillStyle = '#f44336'
      ctx.textAlign = 'center'
      ctx.fillText('Données de direction invalides', canvas.width / 2, canvas.height / 2)
      return
    }
  }
  
  // Préparer les labels et données pour le graphique
  const labels = Object.keys(chartData)
  const data = labels.map(key => chartData[key])
  console.log('Direction chart labels:', labels)
  console.log('Direction chart data:', data)
  
  // Vérifier si nous avons des données avec des valeurs non nulles
  const hasNonZeroData = data.some(value => value > 0)
  
  // Palette de couleurs harmonisée avec la palette turquoise OpSolver
  const elegantColors = [
    '#30C7B5', // Turquoise principal
    '#26A194', // Turquoise foncé
    '#1D7A70', // Turquoise très foncé
    '#155751', // Turquoise ultra foncé
    '#5DD6C7', // Turquoise clair
    '#A9EAE1', // Turquoise très clair
    '#E0F7F4', // Turquoise ultra clair
    '#FF7A68', // Corail doux (complémentaire)
    '#2B8FA6', // Bleu marine (complémentaire)
    '#27B577', // Vert émeraude (complémentaire)
    '#6BBFB4', // Turquoise médium
    '#8EDFD6'  // Turquoise clair médium
  ];
  
  // Garantir suffisamment de couleurs en répétant la palette si nécessaire
  const colors = labels.map((_, i) => elegantColors[i % elegantColors.length]);
  
  // Afficher les données dans la console pour débogage
  console.log('Final direction chart data:', {
    labels: labels,
    data: data,
    colors: colors
  })
  
  // Créer le graphique avec une animation
  directionChart.value = new Chart(ctx, {
    type: 'doughnut', // Graphique en anneau plus élégant
    data: {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: colors,
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 10
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '60%',
      layout: {
        padding: {
          top: 15,
          bottom: 15,
          left: 15,
          right: 15
        }
      },
      animation: {
        animateRotate: true,
        animateScale: true,
        duration: 1500,
        easing: 'easeOutQuart'
      },
      elements: {
        arc: {
          borderWidth: 2
        }
      },
      plugins: {
        legend: {
          position: 'right',
          title: {
            display: true,
            text: `Directions (${directionsCount.value} au total)`,
            font: {
              family: "'Roboto', 'Helvetica', 'Arial', sans-serif",
              size: 14,
              weight: 'bold'
            },
            padding: 10
          },
          labels: {
            boxWidth: 15,
            padding: 15,
            usePointStyle: true,
            pointStyle: 'circle',
            font: {
              family: "'Roboto', 'Helvetica', 'Arial', sans-serif",
              size: 12
            },
            // Assurer que les légendes s'affichent même pour les valeurs à zéro
            filter: (legendItem, data) => true
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: {
            family: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            size: 13
          },
          bodyFont: {
            family: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            size: 12
          },
          padding: 12,
          cornerRadius: 6,
          callbacks: {
            label: function(context) {
              const value = context.raw
              const total = data.reduce((a, b) => a + b, 0)
              const percentage = ((value / total) * 100).toFixed(1)
              return `${context.label}: ${value} agents (${percentage}%)`
            }
          }
        },
        datalabels: {
          display: function(context) {
            // Toujours afficher les labels pour les valeurs non nulles
            return context.dataset.data[context.dataIndex] > 0;
          },
          formatter: function(value, context) {
            if (!context || !context.dataset || !context.dataset.data || context.dataset.data.length === 0) {
              return '';
            }
            const total = context.dataset.data.reduce((a, b) => a + (b || 0), 0);
            if (total === 0) return '0%';
            const percentage = (((value || 0) / total) * 100).toFixed(1);
            return percentage + '%';
          },
          color: '#fff',
          font: {
            weight: 'bold',
            size: 11,
            family: "'Roboto', 'Helvetica', 'Arial', sans-serif"
          },
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          borderRadius: 4,
          padding: { top: 2, bottom: 2, left: 4, right: 4 },
          textStrokeColor: 'black',
          textStrokeWidth: 0.5,
          align: 'center',
          anchor: 'center'
        }
      },
    }
  })
}

// Lifecycle hooks
onMounted(async () => {
  //console.log('Personnel component mounted')
  await fetchPersonnel()
  
  // Charger explicitement les données des directions pour la liste déroulante
  await fetchDirectionData()
  
  await initDirectionChart()
  await fetchGradeData()
})

// Reset filters when component is unmounted
watch([searchQuery, selectedDirection], () => {
  //console.log('Search/Direction changed:', { search: searchQuery.value, direction: selectedDirection.value })
})

</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

/* Styles pour la vue Personnel */
.personnel-view {
  min-height: 100vh;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

.personnel-content {
  padding: 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.personnel-content h4 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.2rem;
  font-weight: 500;
  color: #333;
}

/* Dashboard Section (Stats & Charts) */
.dashboard-section {
  margin-bottom: 24px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
  width: 100%;
}

@media screen and (max-width: 1200px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  background-color: white;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.stat-action {
  position: absolute;
  top: 10px;
  right: 10px;
}

.refresh-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #30C7B5;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.refresh-btn:hover {
  background-color: rgba(48, 199, 181, 0.1);
  transform: rotate(30deg);
}

.refresh-btn .material-icons {
  font-size: 18px;
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 8px;
  margin-right: 16px;
}

.stat-icon .material-icons {
  font-size: 24px;
  color: white;
}

.total-card .stat-icon { background-color: var(--turquoise-primary); } /* Couleur principale turquoise */
.male-card .stat-icon { background-color: var(--turquoise-dark); } /* Turquoise foncé */
.female-card .stat-icon { background-color: var(--coral-soft); } /* Corail doux (complémentaire) */
.direction-card .stat-icon { background-color: var(--blue-marine); } /* Bleu marine (complémentaire) */

.stat-content h3 {
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 600;
}

.stat-content p {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

.stat-percentage {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 20px;
  background-color: #f5f5f5;
}

.stat-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background-color: #f1f1f1;
}

.stat-bar {
  height: 100%;
  transition: width 0.5s ease;
}

.male-card .stat-bar { background-color: #26A194; } /* Turquoise foncé */
.female-card .stat-bar { background-color: #FF7A68; } /* Corail doux (complémentaire) */

/* Charts */
.charts-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
}

@media screen and (max-width: 992px) {
  .charts-container {
    grid-template-columns: 1fr;
  }
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.chart-header {
  margin-bottom: 20px;
}

.chart-header h3 {
  margin: 0;
  font-size: 18px;
  color: #2c3e50;
}

.chart-content {
  height: 300px;
}

/* Control Panel */
.control-panel {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
  width: 100%;
  padding: 20px;
  display: block;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  color: #2c3e50;
}

.result-count {
  font-size: 14px;
  color: #7f8c8d;
}

.panel-content {
  padding: 1rem;
}

/* Styles pour les boutons d'action */
.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  width: 12rem;
}

.btn-primary {
  background-color: #4caf50;
  color: white;
}

.btn-primary:hover {
  background-color: #43a047;
}

.btn-secondary {
  background-color: #2196f3;
  color: white;
}

.btn-secondary:hover {
  background-color: #1e88e5;
}

.btn-secondary:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.search-filters {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 15px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
}

@media (max-width: 768px) {
  .search-filters {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
  
  .action-buttons {
    justify-content: space-between;
    width: 100%;
  }
  
  .action-btn {
    flex: 1;
    justify-content: center;
  }
}

.search-box {
  position: relative;
  min-width: 280px;
}

.search-box input {
  width: 100%;
  padding: 10px 10px 10px 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  outline: none;
  font-size: 14px;
  transition: border-color 0.2s;
}

.search-box input:focus {
  border-color: #30C7B5; /* Couleur principale turquoise */
}

.search-box .material-icons {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9e9e9e;
  font-size: 20px;
}

.filter-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-dropdown label {
  font-size: 14px;
  color: #7f8c8d;
}

.select-wrapper {
  position: relative;
}

.select-wrapper select {
  padding: 10px 36px 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  outline: none;
  font-size: 14px;
  appearance: none;
  background-color: white;
  cursor: pointer;
}

.select-wrapper .select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9e9e9e;
  pointer-events: none;
}

.actions {
  display: flex;
  gap: 10px;
}

.add-btn, .export-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.actions-container {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #4caf50;
  color: white;
}

.btn-primary:hover {
  background-color: #43a047;
}

.btn-secondary {
  background-color: #2196f3;
  color: white;
}

.btn-secondary:hover {
  background-color: #1e88e5;
}

.btn-secondary:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .actions-container {
    width: 100%;
    justify-content: center;
  }
  
  .action-btn {
    flex: 1;
    justify-content: center;
  }
}

/* Table Styles */
.table-container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  margin-bottom: 24px;
}

.table-responsive {
  overflow-x: auto;
}

.personnel-table {
  width: 100%;
  border-collapse: collapse;
}

.personnel-table th, .personnel-table td {
  padding: 12px 16px;
  text-align: left;
}

.personnel-table th {
  background-color: #f9f9f9;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e0e0e0;
}

.personnel-table td {
  border-bottom: 1px solid #f0f0f0;
}

.personnel-table tr:last-child td {
  border-bottom: none;
}

.personnel-table tr:hover {
  background-color: #f5f5f5;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.th-content .material-icons {
  font-size: 18px;
  color: #7f8c8d;
}

.user-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #E0F7F4; /* Turquoise très clair */
  color: #30C7B5; /* Couleur principale turquoise */
  font-weight: 600;
  font-size: 12px;
}

.badge-cell {
  display: flex;
}

.gender-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

.order-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #f0f0f0;
  color: #616161;
  font-size: 12px;
  font-weight: 600;
}

.gender-badge.male {
  background-color: #E0F7F4; /* Turquoise très clair */
  color: #26A194; /* Turquoise foncé */
}

.gender-badge.female {
  background-color: #FFF0ED; /* Version très claire du corail */
  color: #FF7A68; /* Corail doux, complémentaire à la palette turquoise */
}

.grade-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #A9EAE1; /* Turquoise clair */
  color: #155751; /* Turquoise très foncé */
  font-size: 12px;
  font-weight: 500;
}

.capitalize {
  text-transform: capitalize;
}

.action-buttons, .photo-buttons {
  display: flex;
  gap: 8px;
}

.photo-btn {
  border-radius: 4px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  padding: 5px 10px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.2s;
}

.photo-btn:hover {
  background-color: #e9ecef;
}

.photo-btn .material-icons {
  font-size: 1.1rem;
}

.photo-btn .btn-text {
  font-size: 0.8rem;
  display: inline-block;
}

.view-btn, .edit-btn, .delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.view-btn {
  background-color: #e3f2fd;
  color: #2196f3;
}

.edit-btn {
  background-color: #e8f5e9;
  color: #4caf50;
}

.delete-btn {
  background-color: #ffebee;
  color: #f44336;
}

.view-btn:hover { background-color: #bbdefb; }
.edit-btn:hover { background-color: #c8e6c9; }
.delete-btn:hover { background-color: #ffcdd2; }

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
}

/* Styles pour l'affichage des photos des agents */
.agent-name-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.agent-photo {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #E0F7F4;
  background-color: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.agent-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.empty-state .material-icons {
  font-size: 48px;
  color: #bdbdbd;
  margin-bottom: 16px;
}

.empty-state p {
  margin: 0 0 8px;
  font-size: 16px;
  color: #616161;
}

.empty-state small {
  color: #9e9e9e;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  font-size: 14px;
  color: #7f8c8d;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 4px;
  background-color: transparent;
  font-size: 14px;
  color: #616161;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.page-btn:hover {
  background-color: #f5f5f5;
}

.page-btn.active {
  background-color: #2196f3;
  color: white;
}

.page-btn:disabled {
  color: #bdbdbd;
  cursor: not-allowed;
}

.prev .material-icons, .next .material-icons {
  font-size: 20px;
}

/* Debug Section */
.debug-section {
  background-color: #f8f9fa;
  padding: 15px;
  border: 1px solid #ddd;
  margin: 20px 0;
  border-radius: 5px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: repeat(auto-fill, minmax(100%, 1fr));
  }
  
  .panel-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-filters {
    margin-bottom: 16px;
  }
  
  .pagination-container {
    flex-direction: column;
    gap: 16px;
  }
}
</style>
